import CustomerShipmentsApiV2 from 'api/v2/admins/customer-shipments-api-v2'
import { TitlePage } from 'components/atoms'
import { LoaderButton } from 'components/molecules'
import { CardLoader } from 'components/organisms'
import { ALERT_TYPES } from 'constants/alert-constants'
import { SHIPMENT_STATUS } from 'domains/customer-order/constants'
import {
  CustomerOrderScanSkuForm,
  CustomerOrderShipmentAddressPdf,
  CustomerOrderShipmentDetailCard,
  CustomerOrderShipmentItemListTable
} from 'domains/customer-order/organisms'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Row
} from 'reactstrap'
import CustomerOrderShipmentService from 'domains/customer-order/services/shipment/customer-order-shipment-service'
import { useModal } from 'providers/modal-provider'
import TrackShipmentModal from 'domains/customer-shipment/organisms/track-shipment-modal/track-shipment-modal'
import genericModal from 'components/organisms/generic-modal'
import SHIPMENT_METHOD from 'domains/customer-checkout/constants/checkout-shipment-method'
import { useHistory } from 'react-router'
import ZaloraApi from 'api/admins/zalora-api'
import { DOCUMENT_TYPES } from 'domains/zalora/zalora-constants'
import { saveAs } from 'file-saver'
import { DOCUMENT_TEMPLATES } from 'domains/customer-order/constants/customer-order-constants'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const CustomerOrderShipmentDetailPage = ({ pageUtils }) => {
  const history = useHistory()

  const [ shipment, setShipment ]                 = useState({})
  const [ courier, setCourier ]                   = useState({})
  const [ pendingItems, setPendingItems ]         = useState({})
  const [ packedItems, setPackedItems ]           = useState({})
  const [ isLoading, setIsLoading ]               = useState(true)
  const [ isSubmitting, setIsSubmitting ]         = useState({
    ship            : false,
    delivered       : false,
    failed          : false,
    tracking_number : false,
    track_shipment  : false,
    generate_awb    : false,
    print_address   : false
  })
  const [ isSubmitSuccess, setIsSubmitSuccess ]   = useState(false)
  const { id }                                    = useParams()
  const { showModal }                             = useModal()
  const [ isPackSubmitting, setIsPackSubmitting ] = useState(false)

  const handlePackItemBySku = (formikValues, formikBag) => {
    const scannedSku = formikValues.sku
    setIsPackSubmitting(true)
    CustomerShipmentsApiV2.pack(id, scannedSku)
      .then( () => {
        const packedItem = shipment.items.find(({ sku }) => sku === scannedSku)

        const newPendingItems = pendingItems.filter( (pendingItem) => pendingItem.id !== packedItem.id)
        const newPackedItems  = [ ...packedItems, packedItem ]

        setPendingItems(newPendingItems)
        setPackedItems(newPackedItems)
        formikBag.resetForm() // reset form if success

        pageUtils.setAlertMsg(`Item ${scannedSku} has been packed`, ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
      .finally(() => setIsPackSubmitting(false))
  }

  const handleShip = () => {
    setIsSubmitting({ ship: true })
    CustomerShipmentsApiV2.ship(id)
      .then(() => {
        setIsSubmitting({ ship: false })
        setIsSubmitSuccess(true)
        pageUtils.setAlertMsg('Shipment has been SHIPPED', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        setIsSubmitting({ ship: false })
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }

  const handleDelivered = () => {
    setIsSubmitting({ delivered: true })
    CustomerShipmentsApiV2.delivered(id)
      .then(() => {
        setIsSubmitting({ delivered: false })
        setIsSubmitSuccess(true)
        pageUtils.setAlertMsg('Shipment has been DELIVERED', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        setIsSubmitting({ delivered: false })
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }

  const handleFailed = () => {
    setIsSubmitting({ failed: true })
    CustomerShipmentsApiV2.failed(id)
      .then(() => {
        setIsSubmitting({ failed: false })
        setIsSubmitSuccess(true)
        pageUtils.setAlertMsg('Shipment has been FAILED', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        setIsSubmitting({ failed: false })
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }

  const handleUpdateTrackingNumber = (values) => {
    setIsSubmitting({ tracking_number: true })
    CustomerShipmentsApiV2.tracking_number(id, values)
      .then(() => {
        setIsSubmitting({ tracking_number: false })
        setIsSubmitSuccess(true)
        pageUtils.setAlertMsg('Tracking Number has been Updated', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        setIsSubmitting({ tracking_number: false })
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }

  const handleTrackCustomerShipment = () => {
    setIsSubmitting({ track_shipment: true })
    CustomerShipmentsApiV2.tracking(shipment.id)
      .then((response) => {
        const trackingHistory  = response.data.tracking_histories
        const trackingResponse = response.data.tracking_response
        showModal(TrackShipmentModal, {
          shipment         : shipment,
          trackingHistory  : trackingHistory,
          trackingResponse : trackingResponse
        })
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsSubmitting({ track_shipment: false }))
  }

  useEffect(() => {
    CustomerShipmentsApiV2.show(id)
      .then((response) => {
        setShipment(response.data.shipment)
        setPendingItems(response.data.shipment.items.filter((item) => item.status == 'pending' ))
        setPackedItems(response.data.shipment.items.filter((item) => item.status == 'packed' ))
        setIsLoading(false)
        setIsSubmitSuccess(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, isSubmitSuccess, pageUtils ])

  const readyToShip = !isLoading

  const handleClickPrintAddress = () => {
    setIsSubmitting({ track_shipment: true })
    if (shipment.courier === SHIPMENT_METHOD.GRAB_EXPRESS) {
      CustomerShipmentsApiV2.tracking(shipment.id)
        .then((response) => {
          const trackingResponse = response.data.tracking_response
          const courierExist     = !!trackingResponse.courier
          if (courierExist) setCourier(CustomerOrderShipmentService.generateCourierModel(shipment, trackingResponse))
          handlePrintTinkerlustLabel()
        })
        .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
    } else {
      handlePrintTinkerlustLabel()
      setIsSubmitting({ track_shipment: false })
    }
  }

  const handleClickPrintShippingLabel = (template) => {
    switch (template) {
      case DOCUMENT_TEMPLATES.ZALORA:
        handleDownloadZaloraShippingLabel()
        break
      default:
        handleClickPrintAddress()
    }
  }

  const handlePrintTinkerlustLabel = () => {
    //get printable html
    const printableElements = document.getElementById('printAddress').innerHTML
    const addressPdfHTML    = `<html><head><title></title></head><body>${printableElements}</body></html>`
    document.body.innerHTML = addressPdfHTML
    document.title          = `Customer Order Address - ${shipment.recipient_name} - ${shipment.order.number}`
    window.print()

    //reload page when cancel/save/print pdf to back to the shipment detail page
    window.location.reload()
  }

  /**
   * Handles the download of non Tinkerlust template Invoice and convert it to PDF.
   */
  const handleDownloadZaloraShippingLabel = () => {
    let order = shipment.order
    setIsSubmitting({ print_address: true })
    ZaloraApi.downloadDocument({
      orderId      : order.id,
      documentType : DOCUMENT_TYPES.SHIPPING_LABEL
    })
      .then((response) => {
        const blob     = new Blob([ response.data ], { type: 'application/pdf' })
        const fileName = `zalora-order-shipping-label-${ order.number }.pdf`
        saveAs(blob, fileName)
      }).catch((error) => {
        // adding this logic because in `.downloadInvoice` we add responseType in axios
        // if we remove responseType axios the pdf will be blank
        if (error.response && error.response.data instanceof Blob) {
          const reader  = new FileReader()
          reader.onload = () => {
            const errorResponseData = JSON.parse(reader.result)
            pageUtils.setApiErrorMsg(errorResponseData)
          }
          reader.readAsText(error.response.data)
        } else {
          pageUtils.setApiErrorMsg(error.response.data)
        }
      }).finally(() => setIsSubmitting({ print_address: false }))
  }

  const handleClickGenerateAwb = () => {
    if (shipment.courier === SHIPMENT_METHOD.GRAB_EXPRESS) {
      history.push({
        pathname: '/app/request-pickup'
      })
    } else {
      generateShipmentAwb()
    }
  }

  const generateShipmentAwb = () => {
    setIsSubmitting({ generate_awb: true })
    CustomerShipmentsApiV2.generate_awb(shipment.id)
      .then(() => {
        pageUtils.setAlertMsg('Success Generate Awb', ALERT_TYPES.SUCCESS)
        setIsSubmitSuccess(true)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsSubmitting({ generate_awb: false }))
  }

  const showModalImage = (imageUrl) => {
    showModal(genericModal, {
      title    : 'Image',
      children : <img src={ imageUrl } width='100%'/>
    })
  }

  return (
    isLoading ? <CardLoader /> :
      <>
        <TitlePage
          mainTitle={ 'Shipment' }
          subTitle={ `# ${shipment.id}` }
        />
        <Row>
          { /* Left Col - Shipment Items */ }
          <Col md={ 9 }>
            {
              shipment.status === SHIPMENT_STATUS.PENDING ?
                <>

                  <Card>
                    <CardHeader><h4 className='mb-0'>Pending Items</h4></CardHeader>
                    <CustomerOrderShipmentItemListTable
                      data={ pendingItems }
                      hiddenColumns={  shipment.status === SHIPMENT_STATUS.PENDING ? []: [ 'action' ] }
                      defaultPageSize={ 5 }
                      showModalImage={ showModalImage }
                    />
                  </Card>

                  <Card>
                    <CardBody>
                      <h4 className='mb-3'>Ready to Ship Items</h4>
                      <CustomerOrderScanSkuForm
                        shipment={ shipment }
                        buttonText = { 'Pack' }
                        isSubmitting={ isPackSubmitting }
                        handleSubmit={ handlePackItemBySku }
                      />
                    </CardBody>
                    <CustomerOrderShipmentItemListTable
                      data={ packedItems }
                      hiddenColumns={  shipment.status === SHIPMENT_STATUS.PENDING ? []: [ 'action' ] }
                      defaultPageSize={ 5 }
                    />

                    <CardFooter>
                      <div className="float-md-right">
                        <LoaderButton
                          isLoading={ isSubmitting['ship'] }
                          disabled={ !readyToShip || isSubmitting['ship'] }
                          block={ false }
                          color={ readyToShip ? 'success' : 'warning' }
                          onClick={ handleShip }
                          text="SHIP"
                        />
                      </div>
                    </CardFooter>
                  </Card>
                </>
                :
                <>
                  <Card className={ '100vh' }>
                    <CardHeader>Shipped Item</CardHeader>
                    <CardBody>
                      <CustomerOrderShipmentItemListTable
                        data={ shipment.items }
                        hiddenColumns={ [ 'action' ] }
                        defaultPageSize={ 10 }
                        showModalImage={ showModalImage }
                      />
                    </CardBody>
                    {
                      CustomerOrderShipmentService.isCodShipped(shipment) ?
                        <CardFooter>
                          { /* only for COD and status is SHIPPED */ }
                          <div className="float-md-right">
                            <LoaderButton
                              isLoading={ isSubmitting['delivered'] }
                              block={ false }
                              color='success'
                              onClick={ handleDelivered }
                              text="DELIVERED"
                            />
                            <LoaderButton
                              isLoading={ isSubmitting['failed'] }
                              block={ false }
                              color='danger'
                              onClick={ handleFailed }
                              text="FAILED"
                            />
                          </div>
                        </CardFooter> : null
                    }
                  </Card>
                </>
            }
          </Col>

          { /* Right Col - Shipment Details */ }
          <Col md={ 3 } >
            <CustomerOrderShipmentDetailCard
              shipment={ shipment }
              isSubmitting={ isSubmitting }
              handleUpdateTrackingNumber={ handleUpdateTrackingNumber }
              handleClickPrintShippingLabel={ handleClickPrintShippingLabel }
              handleTrackCustomerShipment={ () => handleTrackCustomerShipment() }
              handleClickGenerateAwb={ handleClickGenerateAwb }
            />
          </Col>
        </Row>

        <CustomerOrderShipmentAddressPdf
          shipment={ shipment }
          courier={ courier }
        />
      </>
  )
}

CustomerOrderShipmentDetailPage.propTypes = propTypes

export default CustomerOrderShipmentDetailPage
